<template>
  <div class="gm-panel fight-manager">
    <div class="header">
      <span class="gm-section-title">{{ $t('gmFight.title') }} ({{ fights.length }})</span>
      <div class="header-controls">
        <label class="duels-only">
          <input type="checkbox" :checked="duelsOnly" @change="toggleDuelsOnly">
          <span>{{ $t('gmFight.duelsOnly') }}</span>
        </label>
        <button @click="refresh" class="gm-btn gm-btn--sm gm-btn--ghost" :disabled="isLoading">↻</button>
      </div>
    </div>

    <div v-if="isLoading && fights.length === 0" class="gm-empty">
      <span class="gm-empty__hint">{{ $t('gmFight.loading') }}</span>
    </div>

    <div v-else-if="fights.length === 0" class="gm-empty">
      <span class="gm-empty__title">{{ $t('gmFight.empty.title') }}</span>
      <span class="gm-empty__hint">{{ $t('gmFight.empty.hint') }}</span>
    </div>

    <div v-else class="fights-list">
      <template v-for="fight in fights" :key="fight.id">
      <div class="fight-item">
        <div class="fight-info">
          <div class="participants">
            {{ fight.attacker?.name }} vs {{ fight.defender?.name }}
            <span v-if="fight.duel" class="tag tag--duel">{{ $t('gmFight.duelsOnly') }}</span>
            <span v-if="fight.training" class="tag">{{ $t('gmFight.training') }}</span>
          </div>
          <div class="meta">
            <span class="meta__cell">{{ $t('gmFight.round') }} {{ fight.current_round }}</span>
            <span class="meta__cell">{{ $t('gmFight.phase') }}: {{ phaseLabel(fight) }}</span>
            <span class="meta__cell">
              <CountDown
                  v-if="fight.phase_deadline_at && fight.open !== false"
                  :deadline="fight.phase_deadline_at"
              />
              <template v-else>—</template>
            </span>
            <span v-if="fight.joined && fight.joined.length > 0" class="meta__cell">
              +{{ fight.joined.length }}
            </span>
          </div>
        </div>
        <div class="fight-actions">
          <button @click="toggleSidePanel(fight)" class="gm-btn gm-btn--sm gm-btn--ghost">
            {{ $t('gmFight.sides.toggle') }}
          </button>
          <!-- With the fight worker loop off, this is what moves a sealed
               fight's phase along. Offered only for a sealed fight: an unsealed
               one rides the world cycle and has no clock of its own to step. -->
          <button
              v-if="fight.sealed"
              @click="advancePhase(fight)"
              class="gm-btn gm-btn--sm"
              :disabled="isLoading || advancingFightId === fight.id"
              :title="$t('gmFight.advancePhaseHint')"
          >
            {{ $t('gmFight.advancePhase') }}
          </button>
          <button @click="stopFight(fight)" class="gm-btn gm-btn--sm gm-btn--danger" :disabled="isLoading">
            {{ $t('gmFight.stop') }}
          </button>
        </div>
      </div>

      <div v-if="fight.id === expandedFightId" class="side-panel">
        <div v-if="sidePanelLoading" class="gm-empty__hint">{{ $t('gmFight.sides.loading') }}</div>
        <template v-else>
          <SideSelectionDonut
              ref="sideDonut"
              :sides="sidesForFight(fight.id)"
              :selected-side-id="null"
              @select-side="onDonutSelectSide"
              @create-side="onDonutCreateSide(fight)"
          />

          <div class="side-columns">
            <div
                v-for="side in sidesForFight(fight.id)"
                :key="side.id"
                class="side-column"
                :style="{'--side-color': sideColorVar(side.slot)}"
                @dragover.prevent
                @drop="onDropOnSide(fight, side)"
            >
              <div class="side-column__header">
                <input
                    v-if="renamingSide && renamingSide.fightId === fight.id && renamingSide.sideId === side.id"
                    v-model="renamingSide.draft"
                    class="gm-input side-column__rename-input"
                    @keyup.enter="submitRenameSide(fight)"
                    @keyup.esc="cancelRenameSide"
                    @blur="submitRenameSide(fight)"
                />
                <span v-else class="side-column__name" @click="startRenameSide(fight, side)">
                  {{ side.name }}
                </span>
                <span class="side-column__count">{{ side.member_count }}</span>
                <button
                    class="gm-btn gm-btn--sm gm-btn--ghost side-column__delete"
                    :title="$t('gmFight.sides.delete')"
                    @click="requestDeleteSide(fight, side)"
                >
                  ×
                </button>
              </div>
              <ul class="side-column__members">
                <li
                    v-for="participant in participantsForSide(fight, side.id)"
                    :key="participant.id"
                    class="side-column__member"
                    draggable="true"
                    @dragstart="onDragStart(participant.id, $event)"
                >
                  {{ participant.name }}
                </li>
                <li v-if="participantsForSide(fight, side.id).length === 0" class="side-column__empty">
                  {{ $t('gmFight.sides.empty') }}
                </li>
              </ul>
            </div>
          </div>

          <div v-if="unassignedParticipants(fight).length > 0" class="side-unassigned">
            <span class="gm-empty__hint">{{ $t('gmFight.sides.unassigned') }}</span>
            <ul class="side-column__members">
              <li
                  v-for="participant in unassignedParticipants(fight)"
                  :key="participant.id"
                  class="side-column__member"
                  draggable="true"
                  @dragstart="onDragStart(participant.id, $event)"
              >
                {{ participant.name }}
              </li>
            </ul>
          </div>

          <div class="side-new">
            <input
                v-model="newSideDrafts[fight.id]"
                class="gm-input"
                :placeholder="$t('gmFight.sides.newPlaceholder')"
                @keyup.enter="submitCreateSide(fight)"
            />
            <button
                class="gm-btn gm-btn--sm"
                :disabled="sidesForFight(fight.id).length >= maxFightSides"
                @click="submitCreateSide(fight)"
            >
              {{ $t('gmFight.sides.add') }}
            </button>
          </div>
        </template>
      </div>
      </template>
    </div>

    <!-- Never window.confirm()/alert(): a native dialog blocks the renderer, so
         every countdown in the list above freezes with it. -->
    <ConfirmModal
      :is-open="!!pendingStop"
      :title="$t('gmFight.confirmStop')"
      :message="pendingStopMessage"
      :confirm-label="$t('gmFight.modal.confirmStop')"
      :cancel-label="$t('gmFight.modal.cancel')"
      @confirm="confirmStop"
      @cancel="cancelStop"
    />
    <ConfirmModal
      :is-open="!!stopError"
      mode="alert"
      :title="$t('gmFight.stopFailed')"
      :message="stopError || ''"
      :confirm-label="$t('gmFight.modal.ok')"
      @confirm="stopError = null"
    />
    <ConfirmModal
      :is-open="!!pendingDeleteSide"
      :title="$t('gmFight.sides.confirmDelete')"
      :message="pendingDeleteSide ? pendingDeleteSide.side.name : ''"
      :confirm-label="$t('gmFight.sides.confirmDeleteYes')"
      :cancel-label="$t('gmFight.modal.cancel')"
      @confirm="confirmDeleteSide"
      @cancel="cancelDeleteSide"
    />
    <ConfirmModal
      :is-open="!!sideError"
      mode="alert"
      :title="$t('gmFight.sides.errorTitle')"
      :message="sideError || ''"
      :confirm-label="$t('gmFight.modal.ok')"
      @confirm="sideError = null"
    />
  </div>
</template>

<script>
import { gameMasterFightService } from '@/services/GameMasterFightService.js';
import ConfirmModal from '@/components/Modal/ConfirmModal.vue';
import CountDown from '@/components/Game/Fight/CountDown.vue';
import SideSelectionDonut from '@/components/Fight/SideSelectionDonut.vue';
import {fightPhaseLabel} from '@/utils/fightPhase';
import {sideColorVar} from '@/utils/sideColor';
import {ensureConnection} from '@/api/dx-websocket/index.ts';

const MAX_FIGHT_SIDES = 5;

export default {
  name: 'GameMasterFightManager',
  components: {ConfirmModal, CountDown, SideSelectionDonut},
  props: {
    selectedCharacterData: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      fights: [],
      isLoading: false,
      fightService: gameMasterFightService,
      listeners: null,
      duelsOnly: false,
      pendingStop: null,
      stopError: null,
      advancingFightId: null,
      maxFightSides: MAX_FIGHT_SIDES,
      expandedFightId: null,
      sidePanelLoading: false,
      sidesByFight: {},
      participantsByFight: {},
      newSideDrafts: {},
      renamingSide: null,
      pendingDeleteSide: null,
      draggingCharacterId: null,
      sideError: null
    };
  },
  computed: {
    pendingStopMessage() {
      if (!this.pendingStop) {
        return '';
      }
      return `${this.pendingStop.attacker?.name} vs ${this.pendingStop.defender?.name}`;
    }
  },
  async mounted() {
    this.listeners = {
      loadingStarted: () => { this.isLoading = true; },
      fightsLoaded: (fights) => {
        this.fights = fights || [];
        this.isLoading = false;
      },
      loadingFailed: () => { this.isLoading = false; },
      /* Updates the side panel from the WebSocket, never by polling — the
         events are position-scoped and reach the GM alongside every
         spectator (docs/specs/tactical-combat/05-ui-contract.md §Events).
         The service owns the bus subscription; the component only sees its
         relayed events. */
      fightSideChanged: (data) => this.handleSideUpsertWs(data),
      fightParticipantSideChanged: (data) => this.handleParticipantSideChangedWs(data)
    };

    Object.entries(this.listeners).forEach(([event, handler]) => {
      this.fightService.on(event, handler);
    });

    this.fightService.subscribe(ensureConnection());

    await this.loadFights();
  },
  beforeUnmount() {
    if (this.listeners) {
      Object.entries(this.listeners).forEach(([event, handler]) => {
        this.fightService.off(event, handler);
      });
    }
    this.fightService.unsubscribe();
  },
  methods: {
    phaseLabel(fight) {
      if (!fight?.phase) return '—';
      return fightPhaseLabel(fight.phase, this.$t);
    },

    sideColorVar(slot) {
      return sideColorVar(slot);
    },

    async loadFights() {
      const filters = {
        positionId: this.selectedCharacterData?.position || null,
        /* undefined, not false: the filter is tri-state server side and false
           would ask for "fights that are NOT duels". */
        duel: this.duelsOnly ? true : undefined
      };

      try {
        await this.fightService.loadFights(filters, true);
      } catch (error) {
        this.fights = [];
        this.isLoading = false;
      }
    },

    async toggleDuelsOnly(event) {
      this.duelsOnly = !!event?.target?.checked;
      await this.loadFights();
    },

    async refresh() {
      await this.loadFights();
    },

    /**
     * End this fight's current phase now, without waiting for its deadline.
     *
     * Not behind a confirmation: advancing a phase is what the fight was going
     * to do on its own anyway, and the GM pressing it repeatedly to walk a
     * fight forward should not have to dismiss a modal each time. Stopping a
     * fight keeps its confirmation, because that one cannot be undone.
     */
    async advancePhase(fight) {
      if (this.advancingFightId) {
        return;
      }
      this.advancingFightId = fight.id;
      this.stopError = null;
      try {
        await this.fightService.advanceFightPhase(fight.id);
        await this.loadFights();
      } catch (error) {
        this.stopError = this.$t('gmFight.advancePhaseFailed');
        console.error('[GameMasterFightManager] Advance phase failed:', error);
      } finally {
        this.advancingFightId = null;
      }
    },

    stopFight(fight) {
      this.pendingStop = fight;
    },

    cancelStop() {
      this.pendingStop = null;
    },

    async confirmStop() {
      const fight = this.pendingStop;
      this.pendingStop = null;
      if (!fight) {
        return;
      }
      try {
        await this.fightService.stopFight(fight.id, 'Stopped by GM');
      } catch (error) {
        this.stopError = this.$t('gmFight.stopFailed');
      }
    },

    sidesForFight(fightId) {
      return this.sidesByFight[fightId] || [];
    },

    participantsForSide(fight, sideId) {
      const membership = this.participantsByFight[fight.id] || {};
      return (fight.joined || []).filter(participant => membership[participant.id] === sideId);
    },

    unassignedParticipants(fight) {
      const membership = this.participantsByFight[fight.id] || {};
      return (fight.joined || []).filter(participant => !membership[participant.id]);
    },

    async toggleSidePanel(fight) {
      if (this.expandedFightId === fight.id) {
        this.expandedFightId = null;
        return;
      }
      this.expandedFightId = fight.id;
      this.sideError = null;
      if (!this.sidesByFight[fight.id]) {
        await this.loadSidePanel(fight);
      }
    },

    async loadSidePanel(fight) {
      this.sidePanelLoading = true;
      try {
        const [sides, participants] = await Promise.all([
          this.fightService.listSides(fight.id),
          this.fightService.listParticipants(fight.id)
        ]);
        this.sidesByFight[fight.id] = sides;
        this.participantsByFight[fight.id] = participants.reduce((membership, participant) => {
          membership[participant.character_id] = participant.side_id;
          return membership;
        }, {});
      } catch (error) {
        this.sideError = this.$t('gmFight.sides.loadFailed');
      } finally {
        this.sidePanelLoading = false;
      }
    },

    upsertSide(fightId, side) {
      if (!side || !this.sidesByFight[fightId]) {
        return;
      }
      const sides = this.sidesByFight[fightId];
      const index = sides.findIndex(existing => existing.id === side.id);
      this.sidesByFight[fightId] = index === -1
          ? [...sides, side]
          : sides.map((existing, i) => (i === index ? side : existing));
    },

    startRenameSide(fight, side) {
      this.renamingSide = {fightId: fight.id, sideId: side.id, draft: side.name};
    },

    cancelRenameSide() {
      this.renamingSide = null;
    },

    async submitRenameSide(fight) {
      if (!this.renamingSide || this.renamingSide.fightId !== fight.id) {
        return;
      }
      const {sideId, draft} = this.renamingSide;
      this.renamingSide = null;
      const name = (draft || '').trim();
      if (!name) {
        return;
      }
      try {
        const side = await this.fightService.renameSide(fight.id, sideId, name);
        this.upsertSide(fight.id, side);
      } catch (error) {
        this.sideError = this.$t('gmFight.sides.renameFailed');
      }
    },

    requestDeleteSide(fight, side) {
      this.pendingDeleteSide = {fightId: fight.id, side};
    },

    cancelDeleteSide() {
      this.pendingDeleteSide = null;
    },

    async confirmDeleteSide() {
      const pending = this.pendingDeleteSide;
      this.pendingDeleteSide = null;
      if (!pending) {
        return;
      }
      try {
        await this.fightService.deleteSide(pending.fightId, pending.side.id);
        this.sidesByFight[pending.fightId] = (this.sidesByFight[pending.fightId] || [])
            .filter(existing => existing.id !== pending.side.id);
      } catch (error) {
        /* The only reason a delete is refused is an occupied side — say so
           rather than a generic failure, since the fix is "move them out
           first", not "try again". */
        this.sideError = this.$t('gmFight.sides.deleteFailed');
      }
    },

    async submitCreateSide(fight) {
      const draft = (this.newSideDrafts[fight.id] || '').trim();
      if (!draft) {
        return;
      }
      try {
        const side = await this.fightService.createSide(fight.id, draft);
        this.sidesByFight[fight.id] = [...(this.sidesByFight[fight.id] || []), side];
        this.newSideDrafts[fight.id] = '';
      } catch (error) {
        /* Cap or not, the GM finds out from the same alert modal the rest of
           this panel uses — never a native alert(). */
        this.sideError = this.$t('gmFight.sides.createFailed');
      }
    },

    onDragStart(characterId, event) {
      this.draggingCharacterId = characterId;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = 'move';
        event.dataTransfer.setData('text/plain', characterId);
      }
    },

    async onDropOnSide(fight, side) {
      const characterId = this.draggingCharacterId;
      this.draggingCharacterId = null;
      if (!characterId) {
        return;
      }
      const membership = this.participantsByFight[fight.id] || {};
      if (membership[characterId] === side.id) {
        return;
      }
      try {
        await this.fightService.moveParticipant(fight.id, characterId, side.id);
        this.participantsByFight[fight.id] = {...membership, [characterId]: side.id};
      } catch (error) {
        this.sideError = this.$t('gmFight.sides.moveFailed');
      }
    },

    /* The GM panel repurposes the donut purely as a balance display — moving
       a participant to a side happens by drag, not by "joining" an arc — so
       selecting a wedge here is a no-op rather than a call to a route the GM
       flow has no use for. */
    onDonutSelectSide() {},

    async onDonutCreateSide(fight) {
      const draft = (this.newSideDrafts[fight.id] || '').trim() || this.$t('gmFight.sides.newSideDefaultName');
      try {
        const side = await this.fightService.createSide(fight.id, draft);
        this.sidesByFight[fight.id] = [...(this.sidesByFight[fight.id] || []), side];
        this.newSideDrafts[fight.id] = '';
        this.$refs.sideDonut?.reset?.();
      } catch (error) {
        this.sideError = this.$t('gmFight.sides.createFailed');
        this.$refs.sideDonut?.fail?.(this.sideError);
      }
    },

    handleSideUpsertWs(data) {
      if (!data) {
        return;
      }
      this.upsertSide(data.fight_id, data.side);
    },

    handleParticipantSideChangedWs(data) {
      if (!data || !this.participantsByFight[data.fight_id]) {
        return;
      }
      this.participantsByFight[data.fight_id] = {
        ...this.participantsByFight[data.fight_id],
        [data.character_id]: data.to_side_id ?? null
      };
    }
  },
  watch: {
    'selectedCharacterData.position'() {
      this.loadFights();
    }
  }
};
</script>

<style scoped>
.fight-manager {
  margin-top: var(--gm-gap);
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: var(--gm-gap-sm);
  padding-bottom: var(--gm-gap-sm);
  border-bottom: 1px solid var(--gm-border-dim);
}

.header-controls {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
}

.duels-only {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--gm-text-muted);
  font-size: 0.72rem;
  cursor: pointer;
}

.fights-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

.fight-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
  gap: var(--gm-gap-sm);
}

.fight-item:hover {
  border-color: var(--gm-border);
}

.fight-info {
  flex: 1;
  min-width: 0;
}

.participants {
  color: var(--gm-text);
  font-size: 0.85rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.tag {
  margin-left: 0.4rem;
  padding: 0.05rem 0.35rem;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  font-size: 0.62rem;
  text-transform: uppercase;
  color: var(--gm-text-muted);
}

.tag--duel {
  border-color: rgba(62, 233, 240, 0.5);
  color: var(--hud-cyan, #3ee9f0);
}

.meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  color: var(--gm-text-muted);
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.meta__cell {
  display: inline-flex;
  align-items: center;
  min-height: 1.1rem;
}

.fight-actions {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  align-items: stretch;
}

.side-panel {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: var(--gm-gap-sm);
  margin: -0.25rem 0 0.25rem;
}

.side-columns {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-sm);
}

.side-column {
  flex: 1;
  min-width: 9rem;
  background: var(--gm-surface-2);
  border: 1px solid var(--side-color, var(--gm-border-dim));
  border-radius: var(--gm-radius-sm);
  padding: 0.4rem;
}

.side-column__header {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  border-bottom: 1px solid var(--side-color, var(--gm-border-dim));
  padding-bottom: 0.25rem;
  margin-bottom: 0.3rem;
}

.side-column__name {
  flex: 1;
  min-width: 0;
  color: var(--side-color, var(--gm-text));
  font-size: 0.75rem;
  font-weight: 600;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  cursor: text;
}

.side-column__rename-input {
  flex: 1;
  min-width: 0;
  font-size: 0.75rem;
  padding: 0.1rem 0.3rem;
}

.side-column__count {
  color: var(--gm-text-muted);
  font-size: 0.68rem;
}

.side-column__delete {
  line-height: 1;
  padding: 0 0.35rem;
}

.side-column__members {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-height: 1.4rem;
}

.side-column__member {
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  padding: 0.15rem 0.4rem;
  font-size: 0.72rem;
  color: var(--gm-text);
  cursor: grab;
}

.side-column__empty {
  color: var(--gm-text-dim);
  font-size: 0.68rem;
  font-style: italic;
}

.side-unassigned {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 0.4rem;
  border: 1px dashed var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
}

.side-new {
  display: flex;
  gap: 0.4rem;
}

.side-new .gm-input {
  flex: 1;
}
</style>
